<template>
  <v-container>
    <v-row>
      <v-col>
        <v-text-field
          :label="$t('submissionsView.searchTerm')"
          v-model="query"
          append-icon="mdi-magnify"
          outlined
          clearable
        >
        </v-text-field>
      </v-col>
    </v-row>
    <v-row class="mt-n8">
      <v-col>
        <v-data-table
          :headers="headers"
          :items="filteredSubmissions"
          :search="query"
          :loading="loading"
          :footer-props="dataTableFooterProps"
          :options="{
            sortBy: ['date'],
            sortDesc: [true],
          }"
        >
          <template #[`footer.page-text`]="{ pageStart,pageStop,itemsLength }">
            {{ $t("pageText", { pageStart, pageStop, itemsLength }) }}
          </template>
          <template #[`item.date`]="{ item }">
            {{ item.date | formatDate }}
          </template>
          <template #[`item.price`]="{ item }">
            {{
              locale == "en" ? item._category.title_en : item._category.title
            }}
          </template>
          <template #[`item.reviewer`]="{ item }">
            <v-list
              disabled
              color="transparent"
              dense
              v-if="!loading"
              class="ml-n4"
            >
              <v-list-item
                class="p-0"
                v-for="reviewer in getReviewRatings(item)"
                :key="reviewer.id"
              >
                <v-list-item-icon class="mr-3">
                  <v-icon small left v-if="isAdministrator">
                    mdi-account-circle
                  </v-icon>
                  <v-icon
                    small
                    color="green"
                    v-if="reviewer.state === 'approve'"
                  >
                    mdi-checkbox-marked-circle
                  </v-icon>
                  <v-icon small color="red" v-if="reviewer.state === 'decline'">
                    mdi-minus-circle</v-icon
                  >
                  <v-icon
                    small
                    color="orange"
                    v-if="reviewer.state === 'undecided'"
                  >
                    mdi-help-circle</v-icon
                  >
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title v-if="isAdministrator">
                    {{ getReviewerName(reviewer.reviewerId) }}
                    <span v-if="reviewer.date">
                      ({{ reviewer.date | formatDate }})
                    </span>
                  </v-list-item-title>
                  <v-list-item-title v-else>
                    {{ reviewer.date | formatDate }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn
              tile
              large
              color="primary"
              icon
              @click="showSubmissionEditForm(item)"
            >
              <v-icon v-if="isAdministrator">mdi-account-multiple-plus</v-icon>
              <v-icon v-else>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              tile
              large
              color="primary"
              icon
              @click="showSubmissionDeleteConfirmationForm(item)"
              v-if="isAdministrator"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-dialog v-model="editSubmissionDialog" max-width="800px" persistent>
      <v-card>
        <v-card-title class="white--text headline primary">
          {{
            $t(
              `submissionsView.${isAdministrator ? "assign" : "rate"}Submission`
            )
          }}
          <v-spacer></v-spacer>
          <v-btn icon dark @click="editSubmissionDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row class="mt-2">
            <v-col>
              <v-textarea
                v-model="editSubmission.title"
                :label="$t('form.fields.title')"
                rows="1"
                auto-grow
                outlined
                readonly
              ></v-textarea>
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="editSubmission._category.title"
                :label="$t('form.fields.for')"
                outlined
                readonly
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row class="mt-n8">
            <v-col>
              <v-textarea
                v-model="editSubmission.abstract"
                label="Abstract"
                rows="1"
                auto-grow
                outlined
                readonly
              ></v-textarea>
            </v-col>
          </v-row>
          <v-row class="mt-n8 mb-2">
            <v-col v-if="editSubmission.documents">
              <span
                v-for="document in editSubmission.documents"
                :key="document._id"
              >
                <v-chip
                  class="ma-2"
                  close
                  color="primary"
                  text-color="white"
                  close-icon="mdi-download"
                  @click:close="downloadDocument(document.document)"
                >
                  <v-avatar left>
                    <v-icon small>mdi-file</v-icon>
                  </v-avatar>
                  {{ document.title }}
                </v-chip>
              </span>
            </v-col>
          </v-row>
          <v-expansion-panels class="mb-2">
            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t("form.fields.submitter") }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row class="mt-n4 mb-2">
                  <v-col>
                    <v-text-field
                      v-model="editSubmission.name"
                      :label="$t('form.fields.name')"
                      readonly
                      outlined
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model="editSubmission.email"
                      :label="$t('form.fields.email')"
                      readonly
                      outlined
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row class="mt-n8 mb-2">
                  <v-col>
                    <v-text-field
                      :label="$t('form.fields.street')"
                      outlined
                      readonly
                      v-model="editSubmission.street"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="2">
                    <v-text-field
                      :label="$t('form.fields.zip')"
                      outlined
                      readonly
                      v-model="editSubmission.zip"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-text-field
                      :label="$t('form.fields.city')"
                      outlined
                      readonly
                      v-model="editSubmission.city"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="mt-n8 mb-n8">
                    <v-text-field
                      :label="$t('form.fields.country')"
                      outlined
                      readonly
                      v-model="editSubmission.country"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-row v-if="isAdministrator">
            <v-col>
              <v-autocomplete
                v-model="newReviewerId"
                :items="reviewersValues"
                :label="$t('groups.reviewer')"
                :hint="newReviewerHintText"
                :disabled="reviewersValues.length === 0"
                persistent-hint
                outlined
              ></v-autocomplete>
            </v-col>
            <v-btn
              class="mt-5 mr-3"
              color="primary"
              :disabled="!newReviewerId || reviewersValues.length === 0"
              :loading="assigningReviewer"
              @click="assignReviewer"
            >
              <v-icon left>mdi-account-plus</v-icon>
              {{ $t("form.btn.assign") }}
            </v-btn>
          </v-row>
          <v-row class="mt-n8" v-if="isAdministrator">
            <v-col>
              <v-card v-if="editSubmission.reviewers.length > 0">
                <v-list>
                  <v-list-group
                    v-for="reviewer in editSubmission.reviewers"
                    :key="reviewer._id"
                    no-action
                  >
                    <template v-slot:activator>
                      <v-list-item-icon>
                        <v-icon left>mdi-account-circle</v-icon>
                        <v-icon
                          right
                          color="green"
                          v-if="reviewer.state === 'approve'"
                        >
                          mdi-checkbox-marked-circle
                        </v-icon>
                        <v-icon
                          right
                          color="red"
                          v-if="reviewer.state === 'decline'"
                        >
                          mdi-minus-circle</v-icon
                        >
                        <v-icon
                          right
                          color="orange"
                          v-if="reviewer.state === 'undecided'"
                        >
                          mdi-help-circle</v-icon
                        >
                      </v-list-item-icon>
                      <v-list-item-content v-if="isAdministrator">
                        <v-list-item-title>
                          {{ getReviewerName(reviewer.reviewerId) }}
                          <span v-if="reviewer.date">
                            ({{ reviewer.date | formatDate }})
                          </span>
                        </v-list-item-title>
                      </v-list-item-content>
                    </template>

                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>
                          <div
                            :class="
                              `headline ${
                                reviewer.state === 'decline'
                                  ? 'red'
                                  : reviewer.state === 'approve'
                                  ? 'green'
                                  : reviewer.state === 'undecided'
                                  ? 'orange'
                                  : 'grey'
                              }--text`
                            "
                          >
                            {{
                              $t(
                                `submissionsView.${
                                  reviewer.state ? reviewer.state : "unrated"
                                }`
                              )
                            }}
                          </div>
                          <div class="body-1 mt-2" v-if="reviewer.text">
                            {{ reviewer.text }}
                          </div>
                          <div class="mt-2">
                            <span
                              v-for="document in reviewer.documents"
                              :key="document._id"
                            >
                              <v-chip
                                close
                                color="primary"
                                text-color="white"
                                close-icon="mdi-download"
                                @click:close="
                                  downloadDocument(document.document)
                                "
                              >
                                <v-avatar left>
                                  <v-icon small>mdi-file</v-icon>
                                </v-avatar>
                                {{ document.title }}
                              </v-chip>
                            </span>
                          </div>
                          <div class="mt-2" v-if="reviewer.ratingTable">
                            <v-chip
                              close
                              color="primary"
                              text-color="white"
                              close-icon="mdi-download"
                              @click:close="
                                downloadDocument(reviewer.ratingTable)
                              "
                            >
                              <v-avatar left>
                                <v-icon small>mdi-file-excel</v-icon>
                              </v-avatar>
                              {{ $t("submissionsView.ratingTable") }}
                            </v-chip>
                          </div>
                        </v-list-item-title>
                        <v-list-item-content></v-list-item-content>
                        <v-list-item-action>
                          <v-btn
                            class="ml-n5 mt-n2"
                            color="primary"
                            @click="
                              showDismissReviewerConfirmationForm(
                                reviewer.reviewerId
                              )
                            "
                          >
                            <v-icon left>mdi-account-minus</v-icon>
                            {{ $t("form.btn.dismiss") }}
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list-group>
                </v-list>
              </v-card>
            </v-col>
          </v-row>
          <v-form :lazy-validation="true" v-if="isReviewer">
            <v-row>
              <v-col class="mt-5">
                <v-select
                  :items="ratingValues"
                  v-model="review.state"
                  :label="$t('submissionsView.rating')"
                  outlined
                  :readonly="!review.editable"
                >
                </v-select>
              </v-col>
            </v-row>
            <v-row class="mt-n8">
              <v-col>
                <v-textarea
                  v-model="review.text"
                  :label="$t('form.fields.ratingText')"
                  outlined
                  :readonly="!review.editable"
                ></v-textarea>
              </v-col>
            </v-row>
            <v-row class="mt-n8" v-if="review.editable">
              <v-col>
                <v-file-input
                  v-model="review.documents"
                  accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.zip,.xls,.xlsx"
                  :label="$t('form.fields.documents')"
                  :loading="uploadingReview"
                  multiple
                  small-chips
                  outlined
                  prepend-icon="mdi-paperclip"
                  :hint="$t('submissionsView.documentsHint')"
                  persistent-hint
                ></v-file-input>
              </v-col>
            </v-row>
            <v-row
              class="mt-n12 mb-6"
              v-if="review.oldDocuments && review.oldDocuments.length > 0"
            >
              <v-col>
                <span
                  v-for="document in review.oldDocuments"
                  :key="document._id"
                >
                  <v-chip
                    class="ma-2"
                    close
                    color="primary"
                    text-color="white"
                    close-icon="mdi-download"
                    @click:close="downloadDocument(document.document)"
                  >
                    <v-avatar left>
                      <v-icon small>mdi-file</v-icon>
                    </v-avatar>
                    {{ document.title }}
                    <v-icon
                      right
                      @click="deleteReviewDocument(document)"
                      v-if="review.editable"
                    >
                      mdi-delete
                    </v-icon>
                  </v-chip>
                </span>
              </v-col>
            </v-row>
            <v-row class="mt-n4" v-if="review.editable">
              <v-col>
                <v-file-input
                  v-model="review.ratingTable"
                  accept=".xls,.xlsx"
                  :label="$t('submissionsView.ratingTable') + '*'"
                  :loading="uploadingReview"
                  :hint="$t('submissionsView.ratingTableHint')"
                  small-chips
                  outlined
                  prepend-icon="mdi-paperclip"
                  persistent-hint
                ></v-file-input>
              </v-col>
            </v-row>
            <v-row class="mt-n8" v-if="review.oldRatingTable">
              <v-col>
                <v-chip
                  class="ma-2"
                  close
                  color="primary"
                  text-color="white"
                  close-icon="mdi-download"
                  @click:close="downloadDocument(review.oldRatingTable)"
                >
                  <v-avatar left>
                    <v-icon small>mdi-file-excel</v-icon>
                  </v-avatar>
                  {{ $t("submissionsView.ratingTable") }}
                  <v-icon
                    right
                    @click="review.oldRatingTable = null"
                    v-if="review.editable"
                  >
                    mdi-delete
                  </v-icon>
                </v-chip>
              </v-col>
            </v-row>
            <v-card-actions class="mt-n8" v-if="review.editable">
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                :disabled="!canSendRating()"
                :loading="uploadingReview"
                @click="sendSubmissionDialog = true"
              >
                {{ $t("form.btn.send") }}
              </v-btn>
              <v-btn
                color="primary"
                :disabled="!canSaveRating()"
                :loading="uploadingReview"
                @click="saveRating(false)"
              >
                {{ $t("form.btn.save") }}
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="sendSubmissionDialog" persistent max-width="300px">
      <v-card>
        <v-card-title class="white--text headline primary">
          {{ $t("submissionsView.sendSubmission") }}
        </v-card-title>
        <v-card-text
          class="title pt-5"
          v-html="$t('submissionsView.sendSubmissionText')"
        >
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="saveRating(true)">
            {{ $t("yes") }}
          </v-btn>
          <v-btn color="primary" text @click="sendSubmissionDialog = false">
            {{ $t("no") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="deleteSubmissionDialog" persistent max-width="300px">
      <v-card>
        <v-card-title class="white--text headline primary">
          {{ $t("submissionsView.deleteSubmission") }}
        </v-card-title>
        <v-card-text
          class="title pt-5"
          v-html="
            $t('submissionsView.deleteSubmissionText', {
              title: editSubmission.title,
            })
          "
        >
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="deleteSubmission()">
            {{ $t("yes") }}
          </v-btn>
          <v-btn color="primary" text @click="deleteSubmissionDialog = false">
            {{ $t("no") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="dismissReviewerDialog" persistent max-width="300px">
      <v-card>
        <v-card-title class="white--text headline primary">
          {{ $t("form.btn.dismiss") }}
        </v-card-title>
        <v-card-text
          class="title pt-5"
          v-html="$t('submissionsView.dismissReviewerText')"
        >
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="dismissReviewer()">
            {{ $t("yes") }}
          </v-btn>
          <v-btn color="primary" text @click="dismissReviewerDialog = false">
            {{ $t("no") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import { i18n } from "../plugins/i18n";
import {
  submissionsResource,
  usersGroupsResource,
  submissionReviewResource,
  submissionMailResource,
  attachmentResource,
} from "../api/resources";
import { SubmissionStates } from "../config/constants";
import * as moment from "moment";

export default {
  data: () => ({
    query: null,
    loading: false,
    assigningReviewer: false,
    editSubmissionDialog: false,
    sendSubmissionDialog: false,
    deleteSubmissionDialog: false,
    dismissReviewerDialog: false,
    editSubmission: { _category: {}, reviewers: [] },
    reviewers: [],
    review: {},
    newReviewerDocuments: [],
    newReviewerId: null,
    dismissReviewerId: null,
    newReviewerHintText: "",
    uploadingReview: false,
    headers: [],
    submissions: [],
  }),
  async mounted() {
    this.headers = [
      { text: this.$t("submissionsView.date"), value: "date" },
      { text: this.$t("submissionsView.price"), value: "price" },
      { text: this.$t("submissionsView.submitter"), value: "name" },
      { text: this.$t("submissionsView.title"), value: "title" },
      {
        text: this.$t(
          `submissionsView.${this.isAdministrator ? "reviewer" : "rating"}`
        ),
        value: "reviewer",
        sortable: false,
      },
      { text: "", value: "actions", sortable: false },
    ];
    this.loading = true;
    if (this.isAdministrator) {
      this.reviewers = (
        await usersGroupsResource.get("reviewer", {
          headers: {
            "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
          },
        })
      ).data;
    }
    this.submissions = (
      await submissionsResource.get("", {
        headers: {
          "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
        },
      })
    ).data.results.filter((entry) => entry.date);
    this.loading = false;
  },
  watch: {
    async currentGroups() {
      if (this.isAdministrator) {
        this.reviewers = (
          await usersGroupsResource.get("reviewer", {
            headers: {
              "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
            },
          })
        ).data;
      }
    },
  },
  computed: {
    ...mapGetters([
      "isAdministrator",
      "isReviewer",
      "currentUser",
      "currentGroups",
      "dataTableFooterProps",
    ]),
    locale() {
      return i18n.locale;
    },
    filteredSubmissions() {
      if (this.isReviewer) {
        return this.submissions.filter((submission) =>
          submission.reviewers.find(
            (review) => review.reviewerId === this.currentUser.id
          )
        );
      }
      return this.submissions;
    },
    ratingValues() {
      return SubmissionStates.map((state) => {
        return {
          value: state,
          text: this.$t(`submissionsView.${state}`),
        };
      });
    },
    reviewersValues() {
      return this.reviewers
        .filter((element) => {
          if (this.editSubmission.reviewers.length === 0) {
            return true;
          }
          return (
            this.editSubmission.reviewers
              .map((reviewer) => reviewer.reviewerId)
              .indexOf(element.id) < 0
          );
        })
        .map((reviewer) => ({
          value: reviewer.id,
          text: this.getReviewerName(reviewer.id, true),
        }));
    },
  },
  methods: {
    showSubmissionEditForm(submission) {
      this.editSubmission = submission;
      this.editSubmissionDialog = true;

      if (this.isReviewer) {
        const reviewerElement = this.editSubmission.reviewers.find(
          (reviewer) => reviewer.reviewerId === this.currentUser.id
        );
        this.review = {
          state: reviewerElement.state,
          text: reviewerElement.text,
          oldDocuments: [...reviewerElement.documents],
          oldRatingTable: reviewerElement.ratingTable,
          documents: [],
          ratingTable: null,
          editable: !reviewerElement.date,
        };
      }
    },
    showSubmissionDeleteConfirmationForm(submission) {
      this.editSubmission = submission;
      this.deleteSubmissionDialog = true;
    },
    showDismissReviewerConfirmationForm(reviewerId) {
      this.dismissReviewerId = reviewerId;
      this.dismissReviewerDialog = true;
    },
    getReviewRatings(submission) {
      if (this.isAdministrator) {
        return submission.reviewers;
      }
      return submission.reviewers.filter(
        (reviewer) => reviewer.reviewerId === this.currentUser.id
      );
    },
    canSendRating() {
      return (
        this.review.state &&
        (this.review.text ||
          this.review.documents.length + this.review.oldDocuments.length > 0) &&
        (this.review.ratingTable || this.review.oldRatingTable)
      );
    },
    canSaveRating() {
      if (this.review.documents.length > 0) {
        return true;
      }
      if (this.review.ratingTable) {
        return true;
      }
      const reviewerElement = this.editSubmission.reviewers.find(
        (reviewer) => reviewer.reviewerId === this.currentUser.id
      );
      if (this.review.text && this.review.text !== reviewerElement.text) {
        return true;
      }
      if (this.review.state !== reviewerElement.state) {
        return true;
      }
      if (this.review.oldDocuments.length < reviewerElement.documents.length) {
        return true;
      }
      return false;
    },
    async saveRating(finalize) {
      this.uploadingReview = true;
      const reviewerElement = this.editSubmission.reviewers.find(
        (reviewer) => reviewer.reviewerId === this.currentUser.id
      );
      const documents = this.review.oldDocuments || [];
      for (let index in this.review.documents) {
        const fileData = this.review.documents[index];
        const formData = new FormData();
        formData.append("file", fileData);
        const data = await attachmentResource.post("", formData, {
          headers: { "x-xsrf-token": this.$cookies.get("oegg-cms.csrf") },
        });
        documents.push({
          title: fileData.name,
          document: data.data,
        });
      }
      this.review.documents = [];
      let ratingTable = this.review.oldRatingTable;
      if (this.review.ratingTable) {
        const fileData = this.review.ratingTable;
        const formData = new FormData();
        formData.append("file", fileData);
        const data = await attachmentResource.post("", formData, {
          headers: { "x-xsrf-token": this.$cookies.get("oegg-cms.csrf") },
        });
        ratingTable = data.data;
      }
      await submissionReviewResource.put(
        this.editSubmission._id,
        {
          reviewId: reviewerElement.id,
          state: this.review.state,
          text: this.review.text,
          documents: documents,
          ratingTable: ratingTable,
          finalize: finalize,
        },
        {
          headers: {
            "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
          },
        }
      );
      if (finalize) {
        await submissionReviewResource.post(
          "mail",
          {
            title: this.editSubmission.title,
            reviewerName: `${this.currentUser.username} (${this.currentUser.lastName})`,
            state: this.review.state,
            text: this.review.text,
          },
          {
            headers: {
              "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
            },
          }
        );
        reviewerElement.date = moment().format("YYYY-MM-DD");
        this.review.editable = false;
        this.sendSubmissionDialog = false;
      }
      reviewerElement.state = this.review.state;
      reviewerElement.text = this.review.text;
      reviewerElement.documents = documents;
      reviewerElement.ratingTable = ratingTable;
      this.uploadingReview = false;
    },
    deleteReviewDocument(document) {
      this.review.oldDocuments.splice(
        this.review.oldDocuments.indexOf(document),
        1
      );
    },
    async deleteSubmission() {
      await submissionsResource.delete(this.editSubmission._id, {
        headers: {
          "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
        },
      });
      this.submissions.splice(this.submissions.indexOf(this.editSubmission), 1);
      this.deleteSubmissionDialog = false;
    },
    async dismissReviewer() {
      this.dismissReviewerDialog = false;
      const removeIndex = this.editSubmission.reviewers.findIndex(
        (reviewer) => reviewer.reviewerId === this.dismissReviewerId
      );
      await submissionsResource.patch(
        this.editSubmission._id,
        {
          $pullAllById: {
            reviewers: [this.editSubmission.reviewers[removeIndex].id],
          },
        },
        {
          headers: {
            "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
          },
        }
      );
      this.editSubmission.reviewers.splice(removeIndex, 1);
      // force update of computed properties
      //this.editSubmission.reviewers = [...this.editSubmission.reviewers];
      this.newReviewerHintText = "";
    },
    downloadDocument(document) {
      let fileUrl = document._url;
      if (!fileUrl && document._urls) {
        fileUrl = document._urls.original;
      }
      window.open(fileUrl, "_blank");
    },
    getReviewerName(id, appendUsername) {
      const reviewer = this.reviewers.find((reviewer) => reviewer.id === id);
      if (!reviewer) {
        return "";
      }
      const names = [];
      if (reviewer.firstName) {
        names.push(reviewer.firstName);
      }
      if (reviewer.lastName) {
        names.push(reviewer.lastName);
      }
      if (names.length > 0) {
        return (
          names.join(" ") + (appendUsername ? ` (${reviewer.username})` : "")
        );
      }
      if (reviewer.attributes.company) {
        return (
          reviewer.attributes.company +
          (appendUsername ? ` (${reviewer.username})` : "")
        );
      }
      return reviewer.username;
    },
    async assignReviewer() {
      this.assigningReviewer = true;
      try {
        await submissionsResource.patch(
          this.editSubmission._id,
          {
            $push: {
              reviewers: {
                reviewerId: this.newReviewerId,
              },
            },
          },
          {
            headers: {
              "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
            },
          }
        );
        const reviewer = this.reviewers.find(
          (reviewer) => reviewer.id === this.newReviewerId
        );
        await submissionMailResource.post(
          "",
          {
            ...this.editSubmission,
            categoryName: this.editSubmission._category.title,
            mailReceiver: reviewer.email,
          },
          {
            headers: {
              "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
            },
          }
        );
        this.editSubmission.reviewers.push({ reviewerId: this.newReviewerId });
        this.newReviewerHintText = this.$t(
          "submissionsView.reviewerContacted",
          {
            username: reviewer.username,
          }
        );
      } finally {
        this.assigningReviewer = false;
      }
    },
  },
};
</script>
